import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { ConciergeSidebarWidget } from "@brandclub/bc-widgets";
import { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useDomainConfig, useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { track } from "../../analytics/AnalyticsService.js";
import { AnalyticsTrackingEvent } from "../../analytics/events.js";
import { useConversations } from "../hooks/useConversations.js";
import { useOptionalConciergeContext } from "../ConciergeContextProvider.js";
import { ConfirmActionDialog } from "./ConfirmActionDialog.js";
import { ConversationActionMenu, buildConversationMenuItems } from "./ConversationActionMenu.js";
import { useIsMobile } from "./ConciergeChatAreaV2/useIsMobile.js";
/* empty css                       */
import { resolveBrandclubHostDomain } from "../../utils/domain.js";
import { IconCompose, IconChats, IconChevronUp, IconSidebar, IconHome, IconProfile, IconPurchases, IconLogout } from "./ConciergeIcons.js";
const weightMap = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick == null ? void 0 : onClick();
        },
        "aria-label": ariaLabel,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        className: "tw:flex tw:items-center tw:h-10 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full tw:px-3 tw:gap-2",
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-6", children: icon }),
          isExpanded && /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:font-medium tw:whitespace-nowrap tw:overflow-hidden",
              style: {
                color: resolveColor(state.labelColor),
                fontSize: state.labelFontSize,
                fontWeight: weightMap[state.labelFontWeight] ?? 500,
                lineHeight: "24px"
              },
              children: label
            }
          )
        ]
      }
    ),
    !isExpanded && isHovered && /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
        style: {
          backgroundColor: resolveColor(state.tooltipBgColor),
          color: resolveColor(state.tooltipTextColor),
          fontSize: state.tooltipFontSize,
          fontWeight: 600,
          lineHeight: "18px",
          borderRadius: state.tooltipBorderRadius
        },
        children: label
      }
    )
  ] });
};
const LogoWithTooltip = ({
  state,
  icons,
  resolveColor,
  isExpanded,
  brandLogoUrl,
  onToggle,
  onClick,
  sidebarHovered,
  justCollapsedRef
}) => {
  const showToggleIcon = !isExpanded && !!sidebarHovered && !(justCollapsedRef == null ? void 0 : justCollapsedRef.current);
  const [iconHovered, setIconHovered] = useState(false);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `tw:flex tw:items-center tw:shrink-0 tw:pt-[20px] tw:px-[12px] tw:pb-[24px] ${isExpanded ? "tw:justify-between" : ""}`,
      onClick: (e) => e.stopPropagation(),
      children: [
        !isExpanded && /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onMouseEnter: () => setIconHovered(true),
              onMouseLeave: () => setIconHovered(false),
              className: "tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-lg tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0",
              "aria-label": showToggleIcon ? state.openSidebarLabel : "Home",
              onClick: (e) => {
                e.stopPropagation();
                if (showToggleIcon) {
                  onToggle == null ? void 0 : onToggle();
                } else {
                  onClick == null ? void 0 : onClick();
                }
              },
              children: showToggleIcon ? /* @__PURE__ */ jsx(IconSidebar, { size: state.sidebarIconSize, style: { color: resolveColor(state.sidebarIconColor) } }) : icons.logoIconUrl || brandLogoUrl ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: icons.logoIconUrl || brandLogoUrl,
                  alt: "Brand",
                  className: "tw:object-contain",
                  style: {
                    height: state.logoIconSize,
                    width: state.logoIconSize
                  }
                }
              ) : /* @__PURE__ */ jsx(IconHome, { size: state.logoIconSize, style: { color: resolveColor(state.sidebarIconColor) } })
            }
          ),
          showToggleIcon && iconHovered && /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
              style: {
                backgroundColor: resolveColor(state.tooltipBgColor),
                color: resolveColor(state.tooltipTextColor),
                fontSize: state.tooltipFontSize,
                fontWeight: 600,
                lineHeight: "18px",
                borderRadius: state.tooltipBorderRadius
              },
              children: state.openSidebarLabel
            }
          )
        ] }),
        isExpanded && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              className: "tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0",
              "aria-label": "Home",
              onClick: (e) => {
                e.stopPropagation();
                onClick == null ? void 0 : onClick();
              },
              children: icons.logoIconUrl || brandLogoUrl ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: icons.logoIconUrl || brandLogoUrl,
                  alt: "Brand",
                  className: "tw:object-contain",
                  style: {
                    height: state.logoIconSize,
                    width: state.logoIconSize
                  }
                }
              ) : /* @__PURE__ */ jsx(IconHome, { size: state.logoIconSize, style: { color: resolveColor(state.sidebarIconColor) } })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: (e) => {
                e.stopPropagation();
                onToggle == null ? void 0 : onToggle();
              },
              "aria-label": state.closeSidebarLabel,
              className: "tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:shrink-0 tw:border-none tw:bg-transparent tw:p-0",
              children: /* @__PURE__ */ jsx(IconSidebar, { size: state.sidebarIconSize, style: { color: resolveColor(state.closeIconColor) } })
            }
          )
        ] })
      ]
    }
  );
};
const HistoryItem = ({
  conversation,
  isActive,
  isInLiveTakeover,
  onSelect,
  onDelete,
  onShareChat,
  isEditingTitle,
  editingTitleValue,
  onStartEditTitle,
  onSaveEditTitle,
  onCancelEditTitle,
  state,
  resolveColor
}) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [localEditValue, setLocalEditValue] = useState("");
  const editInputRef = useRef(null);
  const menuRef = useRef(null);
  const dotBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const isMobile = useIsMobile();
  const isThisEditing = isEditingTitle;
  const showKebab = (hovered || menuOpen || isMobile) && !deleting;
  const [lastEditingId, setLastEditingId] = useState(null);
  const currentEditingId = isThisEditing ? conversation.conversationId : null;
  if (currentEditingId !== lastEditingId) {
    setLastEditingId(currentEditingId);
    setLocalEditValue(currentEditingId ? editingTitleValue : "");
  }
  useEffect(() => {
    if (!isThisEditing) return;
    const input = editInputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(0, input.value.length);
  }, [isThisEditing]);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);
  const openMenu = useCallback(() => {
    if (dotBtnRef.current) {
      const rect = dotBtnRef.current.getBoundingClientRect();
      const MENU_WIDTH = 160;
      const MENU_HEIGHT = 120;
      const EDGE_GUTTER = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const preferredLeft = rect.left;
      const clampedLeft = Math.max(EDGE_GUTTER, Math.min(preferredLeft, vw - MENU_WIDTH - EDGE_GUTTER));
      const preferredTop = rect.bottom + 4;
      const clampedTop = Math.min(preferredTop, vh - MENU_HEIGHT - EDGE_GUTTER);
      setDropdownPos({ top: clampedTop, left: clampedLeft });
    }
    setMenuOpen((p) => !p);
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:relative",
      onMouseEnter: isMobile ? void 0 : () => setHovered(true),
      onMouseLeave: isMobile ? void 0 : () => {
        setHovered(false);
        if (!menuOpen) setMenuOpen(false);
      },
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => !deleting && !isThisEditing && onSelect(conversation.conversationId, conversation.mode),
            style: {
              width: "100%",
              textAlign: "left",
              padding: `${state.chatItemPaddingY}px ${state.chatItemPaddingX}px`,
              paddingRight: showKebab || isThisEditing ? 32 : state.chatItemPaddingX,
              backgroundColor: isActive || isThisEditing ? resolveColor(state.chatItemActiveBgColor, "rgba(220,221,220,0.5)") : !isMobile && (hovered || menuOpen) ? resolveColor(state.chatItemHoverBgColor, "rgba(220,221,220,0.3)") : "transparent",
              border: "none",
              cursor: deleting ? "default" : "pointer",
              transition: "background-color 0.15s ease, opacity 0.2s ease",
              borderRadius: state.chatItemBorderRadius,
              display: "block",
              opacity: deleting ? 0.5 : 1
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                className: "tw:flex tw:items-center tw:gap-2",
                style: {
                  fontSize: state.chatItemFontSize,
                  fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                  color: resolveColor(state.chatItemColor),
                  lineHeight: "24px"
                },
                children: [
                  deleting && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      className: "tw:shrink-0",
                      style: { animation: "spin 1s linear infinite" },
                      children: /* @__PURE__ */ jsx(
                        "circle",
                        {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: resolveColor(state.chatItemColor),
                          strokeWidth: "3",
                          strokeLinecap: "round",
                          strokeDasharray: "32",
                          strokeDashoffset: "12"
                        }
                      )
                    }
                  ),
                  isThisEditing ? /* @__PURE__ */ jsx(
                    "input",
                    {
                      ref: editInputRef,
                      type: "text",
                      value: localEditValue,
                      onChange: (e) => setLocalEditValue(e.target.value),
                      onBlur: () => {
                        const trimmed = localEditValue.trim();
                        if (trimmed && trimmed !== (conversation.title || conversation.preview)) {
                          onSaveEditTitle(conversation.conversationId, trimmed);
                        } else {
                          onCancelEditTitle();
                        }
                      },
                      onKeyDown: (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = localEditValue.trim();
                          if (trimmed) {
                            onSaveEditTitle(conversation.conversationId, trimmed);
                          } else {
                            onCancelEditTitle();
                          }
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          onCancelEditTitle();
                        }
                      },
                      onClick: (e) => e.stopPropagation(),
                      onFocus: (e) => {
                        e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
                      },
                      style: {
                        width: "100%",
                        flex: 1,
                        minWidth: 0,
                        padding: 0,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: state.chatItemFontSize,
                        fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                        color: resolveColor(state.chatItemColor),
                        lineHeight: "24px"
                      },
                      "aria-label": "Edit conversation name"
                    }
                  ) : /* @__PURE__ */ jsxs(Fragment, { children: [
                    isInLiveTakeover && /* @__PURE__ */ jsxs(
                      "span",
                      {
                        className: "tw:relative tw:flex tw:h-1.5 tw:w-1.5 tw:shrink-0",
                        "aria-label": "Connected to support team",
                        children: [
                          /* @__PURE__ */ jsx(
                            "span",
                            {
                              className: "tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-ping tw:rounded-full tw:opacity-75",
                              style: { backgroundColor: "var(--bc-support-dot)" }
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "span",
                            {
                              className: "tw:relative tw:inline-flex tw:h-1.5 tw:w-1.5 tw:rounded-full",
                              style: { backgroundColor: "var(--bc-support-dot)" }
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        style: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: state.chatItemLineClamp,
                          WebkitBoxOrient: "vertical"
                        },
                        children: conversation.title || conversation.preview || "New chat"
                      }
                    )
                  ] })
                ]
              }
            )
          }
        ),
        showKebab && /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:right-1 tw:top-1/2 tw:-translate-y-1/2", children: /* @__PURE__ */ jsx(
          "button",
          {
            ref: dotBtnRef,
            onClick: (e) => {
              e.stopPropagation();
              openMenu();
            },
            className: "tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/10 tw:transition-colors",
            "aria-label": "More options",
            children: /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "3", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "13", r: "1.5", fill: resolveColor(state.chatItemColor) })
            ] })
          }
        ) }),
        menuOpen && dropdownPos && ReactDOM.createPortal(
          /* @__PURE__ */ jsx(
            ConversationActionMenu,
            {
              ref: menuRef,
              fontFamily: state.fontFamilyCss,
              items: buildConversationMenuItems({
                onShare: () => {
                  setMenuOpen(false);
                  onShareChat == null ? void 0 : onShareChat(conversation.conversationId);
                },
                onRename: () => {
                  setMenuOpen(false);
                  onStartEditTitle(conversation.conversationId, conversation.title || conversation.preview || "");
                },
                onDelete: () => {
                  setMenuOpen(false);
                  setDeleteConfirmOpen(true);
                },
                defaultColor: resolveColor(state.chatItemColor)
              }),
              style: { position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }
            }
          ),
          document.body
        ),
        /* @__PURE__ */ jsx(
          ConfirmActionDialog,
          {
            open: deleteConfirmOpen,
            title: "Are you sure? This action cannot be undone.",
            message: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Continuing will delete ",
              /* @__PURE__ */ jsx("strong", { children: conversation.title || "this conversation" }),
              "."
            ] }),
            confirmText: "Delete",
            backgroundColor: resolveColor(state.expandedBgColor),
            primaryColor: resolveColor(state.chatItemColor),
            primaryForegroundColor: resolveColor(state.tooltipTextColor),
            fontFamily: state.fontFamilyCss,
            onCancel: () => setDeleteConfirmOpen(false),
            onConfirm: async () => {
              setDeleteConfirmOpen(false);
              setDeleting(true);
              try {
                await onDelete(conversation.conversationId);
              } finally {
                setDeleting(false);
              }
            }
          }
        )
      ]
    }
  );
};
const ProfileMenuItems = ({
  onProfileClick,
  onPurchasesClick,
  onSignOutClick,
  signOutLabel,
  labelColor,
  resolveColor,
  onClose
}) => {
  const color = resolveColor(labelColor);
  const itemStyle = { gap: 8, padding: "4px", fontFamily: "inherit" };
  const labelStyle = { fontSize: 14, fontWeight: 500, color, lineHeight: "24px", whiteSpace: "nowrap" };
  return /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col", style: { padding: "4px" }, children: [
    onProfileClick && /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClose();
          onProfileClick();
        },
        className: "tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md",
        style: itemStyle,
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0", style: { width: 24, height: 24 }, children: /* @__PURE__ */ jsx(IconProfile, { size: 18, style: { color } }) }),
          /* @__PURE__ */ jsx("span", { style: labelStyle, children: "Profile" })
        ]
      }
    ),
    onPurchasesClick && /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClose();
          onPurchasesClick();
        },
        className: "tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md",
        style: itemStyle,
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0", style: { width: 24, height: 24 }, children: /* @__PURE__ */ jsx(IconPurchases, { size: 18, style: { color } }) }),
          /* @__PURE__ */ jsx("span", { style: labelStyle, children: "Purchases" })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { style: { height: 0, borderTop: "1px solid #e8e8ed", margin: "4px -4px" } }),
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClose();
          onSignOutClick == null ? void 0 : onSignOutClick();
        },
        className: "tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md",
        style: itemStyle,
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0", style: { width: 24, height: 24 }, children: /* @__PURE__ */ jsx(IconLogout, { size: 18, style: { color } }) }),
          /* @__PURE__ */ jsx("span", { style: labelStyle, children: signOutLabel })
        ]
      }
    )
  ] });
};
const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  onChatsClick,
  currentConversationId,
  userProfile,
  apiBaseUrl,
  onSignOutClick,
  onProfileClick,
  onPurchasesClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle,
  mobileOverlay = false,
  onShareChat,
  onSignInClick,
  liveTakeoverActiveConversationId
}) => {
  const isMobile = useIsMobile();
  const conciergeContext = useOptionalConciergeContext();
  const resolvedApiBaseUrl = apiBaseUrl ?? (conciergeContext == null ? void 0 : conciergeContext.apiBaseUrl) ?? resolveBrandclubHostDomain();
  const { data: domainConfig } = useDomainConfig();
  const { data: brandAppConfig } = useBrandAppConfig(resolvedApiBaseUrl);
  const brandLinkTextFromConfig = (() => {
    var _a;
    if (domainConfig == null ? void 0 : domainConfig.customShopDomainName) {
      return domainConfig.customShopDomainName;
    }
    const link = (_a = brandAppConfig == null ? void 0 : brandAppConfig.branchIOConfig) == null ? void 0 : _a.webAppBranchLink;
    if (!link) return void 0;
    try {
      return new URL(link).hostname;
    } catch {
      return link.replace(/^https?:\/\//, "").replace(/\/+$/, "");
    }
  })();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = isExpandedProp !== void 0;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;
  const justCollapsedRef = useRef(false);
  const collapseTimerRef = useRef();
  const handleToggle = () => {
    if (isExpanded) {
      justCollapsedRef.current = true;
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = setTimeout(() => {
        justCollapsedRef.current = false;
      }, 400);
    }
    if (isControlled) onToggle == null ? void 0 : onToggle();
    else setInternalExpanded((p) => !p);
  };
  const {
    conversations,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    deleteMutation,
    renameMutation,
    refresh: refreshConversations
  } = useConversations({ apiBaseUrl: resolvedApiBaseUrl });
  const STALE_THRESHOLD_MS = 5 * 6e4;
  const shouldPollConversations = conversations.some((c) => {
    if (c.status !== "in_progress") return false;
    const age = Date.now() - new Date(c.lastUpdated).getTime();
    return age < STALE_THRESHOLD_MS;
  });
  useEffect(() => {
    if (!isExpanded || !shouldPollConversations) return;
    const intervalId = window.setInterval(() => refreshConversations(), 3e3);
    return () => window.clearInterval(intervalId);
  }, [isExpanded, shouldPollConversations, refreshConversations]);
  const loadMoreSentinelRef = useRef(null);
  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || !isExpanded || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        var _a;
        if (((_a = entries[0]) == null ? void 0 : _a.isIntersecting) && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, hasNextPage, isFetchingNextPage, fetchNextPage]);
  const handleDeleteConversation = useCallback(
    async (conversationId) => {
      try {
        await deleteMutation.mutateAsync([conversationId]);
        track(AnalyticsTrackingEvent.CONCIERGE_CONVERSATION_DELETE, {
          conversationId,
          pageUrl: typeof window !== "undefined" ? window.location.href : ""
        }).catch(() => {
        });
        if (currentConversationId === conversationId) {
          onComposeClick == null ? void 0 : onComposeClick();
        }
      } catch {
      }
    },
    [deleteMutation, currentConversationId, onComposeClick]
  );
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const handleStartEditTitle = useCallback((conversationId, title) => {
    setEditingConversationId(conversationId);
    setEditingTitleValue(title);
  }, []);
  const handleSaveEditTitle = useCallback(
    async (conversationId, newTitle) => {
      setEditingConversationId(null);
      setEditingTitleValue("");
      try {
        await renameMutation.mutateAsync({ conversationId, title: newTitle });
      } catch {
      }
    },
    [renameMutation]
  );
  const handleCancelEditTitle = useCallback(() => {
    setEditingConversationId(null);
    setEditingTitleValue("");
  }, []);
  const getInitials = () => {
    const f = (userProfile == null ? void 0 : userProfile.firstName) || "";
    const l = (userProfile == null ? void 0 : userProfile.lastName) || "";
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    return "";
  };
  const initials = getInitials();
  const displayName = [userProfile == null ? void 0 : userProfile.firstName, userProfile == null ? void 0 : userProfile.lastName].filter(Boolean).join(" ") || "";
  const isSignedIn = (userProfile == null ? void 0 : userProfile.isSignedIn) ?? false;
  const prevIsSignedInRef = useRef(void 0);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    prevIsSignedInRef.current = isSignedIn;
    if (wasSignedIn === false && isSignedIn) {
      refreshConversations();
    }
  }, [isSignedIn, refreshConversations]);
  const [avatarHovered, setAvatarHovered] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileBtnRef = useRef(null);
  const profileMenuRef = useRef(null);
  const [profileMenuPos, setProfileMenuPos] = useState(null);
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target) && profileBtnRef.current && !profileBtnRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileMenuOpen]);
  return /* @__PURE__ */ jsx(
    ConciergeSidebarWidget,
    {
      widgetId: "concierge-sidebar",
      displayName: "Concierge Sidebar",
      wrapperStyle: {
        width: "auto",
        flexShrink: 0,
        display: "flex",
        height: "100%"
      },
      children: (widgetState, icons, resolveColor) => {
        const state = brandLinkTextFromConfig ? { ...widgetState, brandLinkText: brandLinkTextFromConfig } : widgetState;
        return /* @__PURE__ */ jsxs(
          "aside",
          {
            role: "complementary",
            "aria-expanded": isExpanded,
            onClick: !isExpanded ? handleToggle : void 0,
            onMouseEnter: () => !isExpanded && setSidebarHovered(true),
            onMouseLeave: () => setSidebarHovered(false),
            className: mobileOverlay ? "tw:flex tw:flex-col tw:shrink-0 tw:h-full" : "tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full",
            style: {
              backgroundColor: resolveColor(isExpanded ? state.expandedBgColor : state.collapsedBgColor),
              width: isExpanded ? state.expandedWidth : state.collapsedWidth,
              transition: "width 0.25s ease-in-out",
              cursor: isExpanded ? void 0 : "pointer",
              borderRight: `1px solid ${resolveColor(state.borderColor)}`,
              boxSizing: "content-box",
              overflow: "hidden",
              ...state.fontFamilyCss && state.fontFamilyCss !== "inherit" ? { fontFamily: state.fontFamilyCss } : {}
            },
            children: [
              /* @__PURE__ */ jsx(
                LogoWithTooltip,
                {
                  state,
                  icons,
                  resolveColor,
                  isExpanded,
                  brandLogoUrl,
                  onToggle: handleToggle,
                  onClick: onComposeClick,
                  sidebarHovered,
                  justCollapsedRef
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col", children: [
                /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconCompose, { size: state.composeIconSize, style: { color: resolveColor(state.composeIconColor) } }),
                    label: state.newChatLabel,
                    onClick: onComposeClick,
                    isExpanded,
                    ariaLabel: state.newChatLabel,
                    state,
                    resolveColor
                  }
                ),
                /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconChats, { size: state.chatsIconSize, style: { color: resolveColor(state.chatsIconColor) } }),
                    label: state.chatsLabel,
                    onClick: onChatsClick,
                    isExpanded,
                    ariaLabel: state.chatsLabel,
                    state,
                    resolveColor
                  }
                )
              ] }),
              isExpanded ? /* @__PURE__ */ jsxs("div", { className: "sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden tw:mt-[24px]", children: [
                (isLoading || conversations.length > 0) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:px-4 tw:pb-1 tw:shrink-0",
                    style: {
                      fontSize: state.chatHistoryTitleFontSize,
                      fontWeight: weightMap[state.chatHistoryTitleFontWeight] ?? 500,
                      color: resolveColor(state.chatHistoryTitleColor),
                      lineHeight: "24px",
                      whiteSpace: "nowrap"
                    },
                    children: state.chatHistoryTitle
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-y-auto tw:mt-1 tw:px-2 concierge-conversation-scroll", children: [
                  isLoading && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:justify-center tw:py-4", children: /* @__PURE__ */ jsx(LoadingSpinner, { color: resolveColor(state.chatHistoryTitleColor) }) }),
                  !isLoading && conversations.length > 0 && /* @__PURE__ */ jsxs("div", { className: "sidebar-history-list", children: [
                    conversations.map((c) => /* @__PURE__ */ jsx(
                      HistoryItem,
                      {
                        conversation: c,
                        isActive: c.conversationId === currentConversationId,
                        isInLiveTakeover: c.conversationId === liveTakeoverActiveConversationId,
                        onSelect: (id, mode) => onConversationSelect == null ? void 0 : onConversationSelect(id, mode),
                        onDelete: handleDeleteConversation,
                        onShareChat,
                        isEditingTitle: editingConversationId === c.conversationId,
                        editingTitleValue,
                        onStartEditTitle: handleStartEditTitle,
                        onSaveEditTitle: handleSaveEditTitle,
                        onCancelEditTitle: handleCancelEditTitle,
                        state,
                        resolveColor
                      },
                      c.conversationId
                    )),
                    hasNextPage && /* @__PURE__ */ jsx(
                      "div",
                      {
                        ref: loadMoreSentinelRef,
                        className: "tw:flex tw:justify-center tw:py-2",
                        style: { minHeight: 1 },
                        children: isFetchingNextPage && /* @__PURE__ */ jsx(LoadingSpinner, { color: resolveColor(state.chatHistoryTitleColor) })
                      }
                    )
                  ] })
                ] })
              ] }) : /* @__PURE__ */ jsx("div", { className: "tw:flex-1" }),
              isSignedIn ? /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "tw:relative tw:mt-auto tw:shrink-0",
                  onClick: (e) => e.stopPropagation(),
                  style: {
                    borderTop: isExpanded ? `1px solid ${resolveColor(state.borderColor)}` : void 0,
                    backgroundColor: isExpanded ? resolveColor(state.expandedBgColor) : void 0
                  },
                  children: [
                    profileMenuOpen && isExpanded && /* @__PURE__ */ jsx(
                      "div",
                      {
                        ref: profileMenuRef,
                        className: "tw:absolute tw:bottom-full tw:z-50",
                        style: {
                          left: 16,
                          marginBottom: 8,
                          width: 216,
                          backgroundColor: "#ffffff",
                          border: "1px solid #e8e8ed",
                          borderRadius: 5,
                          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
                          fontFamily: "inherit"
                        },
                        children: /* @__PURE__ */ jsx(
                          ProfileMenuItems,
                          {
                            onProfileClick,
                            onPurchasesClick,
                            onSignOutClick,
                            signOutLabel: state.signOutLabel,
                            labelColor: state.labelColor,
                            resolveColor,
                            onClose: () => setProfileMenuOpen(false)
                          }
                        )
                      }
                    ),
                    /* @__PURE__ */ jsxs(
                      "button",
                      {
                        ref: profileBtnRef,
                        "aria-label": displayName || state.signOutLabel,
                        onClick: (e) => {
                          e.stopPropagation();
                          if (!isExpanded && profileBtnRef.current) {
                            const rect = profileBtnRef.current.getBoundingClientRect();
                            setProfileMenuPos({ top: rect.top - 4, left: rect.left + 4 });
                          }
                          setProfileMenuOpen((p) => !p);
                        },
                        onMouseEnter: () => setAvatarHovered(true),
                        onMouseLeave: () => setAvatarHovered(false),
                        className: `tw:flex tw:items-center tw:cursor-pointer tw:w-full tw:rounded-none tw:transition-colors hover:tw:bg-black/5 tw:border-none tw:bg-transparent tw:overflow-hidden ${isExpanded ? "tw:h-16 tw:px-4 tw:gap-3" : "tw:py-2 tw:px-2 tw:mb-2"}`,
                        style: isMobile && isExpanded ? { height: 65 } : void 0,
                        children: [
                          /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: "tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0",
                              style: {
                                width: state.avatarSize,
                                height: state.avatarSize,
                                backgroundColor: resolveColor(state.avatarBgColor),
                                color: resolveColor(state.avatarTextColor),
                                fontSize: state.avatarFontSize,
                                fontWeight: weightMap[state.avatarFontWeight] ?? 600,
                                lineHeight: "1.5"
                              },
                              children: initials
                            }
                          ),
                          isExpanded && /* @__PURE__ */ jsx(
                            "span",
                            {
                              className: "tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis tw:flex-1 tw:text-left",
                              style: {
                                color: resolveColor(state.labelColor),
                                fontSize: 14,
                                fontWeight: weightMap[state.labelFontWeight] ?? 500,
                                lineHeight: "24px"
                              },
                              children: displayName || state.signOutLabel
                            }
                          ),
                          isExpanded && /* @__PURE__ */ jsx(
                            IconChevronUp,
                            {
                              size: 18,
                              style: {
                                color: resolveColor(state.labelColor),
                                transform: profileMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                                flexShrink: 0
                              }
                            }
                          )
                        ]
                      }
                    ),
                    !isExpanded && avatarHovered && !profileMenuOpen && /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
                        style: {
                          backgroundColor: resolveColor(state.tooltipBgColor),
                          color: resolveColor(state.tooltipTextColor),
                          fontSize: state.tooltipFontSize,
                          fontWeight: 600,
                          lineHeight: "18px",
                          borderRadius: state.tooltipBorderRadius
                        },
                        children: displayName || state.signOutLabel
                      }
                    ),
                    profileMenuOpen && !isExpanded && profileMenuPos && ReactDOM.createPortal(
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          ref: profileMenuRef,
                          className: "tw:fixed tw:z-[9999]",
                          style: {
                            top: profileMenuPos.top,
                            left: profileMenuPos.left,
                            transform: "translateY(-100%)",
                            width: 216,
                            backgroundColor: "#ffffff",
                            border: "1px solid #e8e8ed",
                            borderRadius: 5,
                            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
                            fontFamily: state.fontFamilyCss && state.fontFamilyCss !== "inherit" ? state.fontFamilyCss : void 0
                          },
                          children: /* @__PURE__ */ jsx(
                            ProfileMenuItems,
                            {
                              onProfileClick,
                              onPurchasesClick,
                              onSignOutClick,
                              signOutLabel: state.signOutLabel,
                              labelColor: state.labelColor,
                              resolveColor,
                              onClose: () => setProfileMenuOpen(false)
                            }
                          )
                        }
                      ),
                      document.body
                    )
                  ]
                }
              ) : /* @__PURE__ */ jsx(
                "div",
                {
                  className: "tw:relative tw:mt-auto tw:shrink-0",
                  onClick: (e) => e.stopPropagation(),
                  style: {
                    borderTop: isExpanded ? "1px solid #dcdddc" : void 0,
                    backgroundColor: isExpanded ? resolveColor(state.expandedBgColor) : void 0
                  },
                  children: isExpanded && /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: "tw:flex tw:flex-col tw:items-stretch",
                      style: {
                        padding: "20px 12px",
                        width: state.expandedWidth - 1,
                        boxSizing: "border-box"
                      },
                      children: [
                        /* @__PURE__ */ jsx(
                          "p",
                          {
                            style: {
                              fontSize: 14,
                              fontWeight: 500,
                              color: resolveColor(state.labelColor),
                              lineHeight: "24px",
                              margin: "0 0 16px",
                              whiteSpace: "nowrap"
                            },
                            children: "Get responses tailored to you"
                          }
                        ),
                        /* @__PURE__ */ jsxs(
                          "p",
                          {
                            style: {
                              fontSize: 12,
                              fontWeight: 400,
                              color: resolveColor(state.labelColor),
                              lineHeight: "18px",
                              margin: "0 0 24px",
                              whiteSpace: "nowrap"
                            },
                            children: [
                              "Log in to access your profile and",
                              /* @__PURE__ */ jsx("br", {}),
                              "get personalized assistance."
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "button",
                          {
                            onClick: (e) => {
                              e.stopPropagation();
                              onSignInClick == null ? void 0 : onSignInClick();
                            },
                            className: "tw:cursor-pointer tw:border tw:border-solid tw:border-[#dcdddc] tw:rounded-full tw:transition-all tw:duration-200 tw:bg-white",
                            style: {
                              padding: "8px 12px",
                              fontSize: 14,
                              fontWeight: 600,
                              color: resolveColor(state.labelColor),
                              fontFamily: "inherit",
                              lineHeight: "20px",
                              whiteSpace: "nowrap"
                            },
                            children: "Sign in"
                          }
                        )
                      ]
                    }
                  )
                }
              )
            ]
          }
        );
      }
    }
  );
};
const LoadingSpinner = ({ color }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", style: { animation: "spin 1s linear infinite" }, children: [
  /* @__PURE__ */ jsx("style", { children: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }),
  /* @__PURE__ */ jsx(
    "circle",
    {
      cx: "12",
      cy: "12",
      r: "10",
      stroke: color,
      strokeWidth: "3",
      strokeLinecap: "round",
      strokeDasharray: "32",
      strokeDashoffset: "12"
    }
  )
] });
export {
  ConciergeSidebar
};
//# sourceMappingURL=ConciergeSidebar.js.map
